// bluaIQ — Gantt generic construction phase ordering
//
// DISPLAY-ONLY, FRONTEND-ONLY. Ranks a phase label into a coherent residential
// construction sequence so the Gantt left task list reads logically even when
// the backend `sortOrder` interleaves phases (e.g. an inspection pack that
// alternates under-slab / foundation / under-slab). It does NOT reorder tasks
// within a phase, does NOT change dates, and creates/persists nothing.
//
// Generic only: every keyword below is a construction *stage* term (site,
// foundation, framing, MEP, drywall, finishes, closeout, …) or its Spanish
// equivalent. There are NO city / jurisdiction / location names here — this is
// not a jurisdiction pack and carries no location-specific logic.
//
// Pure function — safe to unit-test without React.

// Phases the rank function does not recognise sort *after* all ranked phases,
// keeping their original (first-encounter / sortOrder) order via a stable sort.
export const PHASE_UNKNOWN_RANK = 9999

// Some schedules store the phase as a NUMERIC macro-stage key ("0".."4") — the
// default project lifecycle (seedLifecycleTasks) groups tasks into a 5-stage
// builder lifecycle and persists `phase` as the stage number. These keys are
// not descriptive, so without a mapping the UI would (a) show a raw "2" as the
// group header and (b) treat every numeric phase as unknown (no order). This
// map gives each numeric stage a readable bilingual label and a rank so the
// groups read and sort coherently. The labels are confirmed by the actual
// seeded task names in each stage (land/due-diligence, plans/permits,
// bids/contracts/insurance, construction, closeout/sale). Generic only — no
// city / jurisdiction / location specifics.
const NUMERIC_PHASE_LABELS: Record<string, { rank: number; en: string; es: string }> = {
  '0': { rank: 5, en: 'Land / Due Diligence', es: 'Terreno / Debida diligencia' },
  '1': { rank: 8, en: 'Plans & Permits', es: 'Planos y Permisos' },
  '2': { rank: 9, en: 'Bids, Contracts & Insurance', es: 'Propuestas, Contratos y Seguros' },
  '3': { rank: 30, en: 'Construction', es: 'Construcción' },
  '4': { rank: 100, en: 'Closeout / Sale', es: 'Cierre / Venta' },
}

// Descriptive (non-numeric) phase labels → bilingual headers. Auto-Generate
// Classic and the demo schedule store descriptive English phase keys; without a
// mapping the Spanish UI would show the English header (the stored phase has no
// Spanish variant). This gives the generic builder phases — preconstruction,
// every fine construction stage, and closeout — a readable EN/ES header. ES
// values match the demo's phaseEs for the shared stages so demo display is
// unchanged. Generic construction stages only — no city/jurisdiction names.
const DESCRIPTIVE_PHASE_LABELS: Record<string, { en: string; es: string }> = {
  // Preconstruction
  'Land / Due Diligence': { en: 'Land / Due Diligence', es: 'Terreno / Debida diligencia' },
  'Plans & Permits': { en: 'Plans & Permits', es: 'Planos y Permisos' },
  'Bids, Contracts & Insurance': { en: 'Bids, Contracts & Insurance', es: 'Propuestas, Contratos y Seguros' },
  // Construction — fine stages
  'Site Work': { en: 'Site Work', es: 'Trabajo de Sitio' },
  'Foundation': { en: 'Foundation', es: 'Cimentación' },
  'Framing': { en: 'Framing', es: 'Estructura' },
  'Roofing': { en: 'Roofing', es: 'Techo' },
  'MEP Rough-In': { en: 'MEP rough-in', es: 'Instalaciones MEP — rough-in' },
  'Insulation': { en: 'Insulation', es: 'Aislamiento' },
  'Drywall': { en: 'Drywall', es: 'Tablaroca' },
  'Interior Paint': { en: 'Interior Paint', es: 'Pintura Interior' },
  'Flooring': { en: 'Flooring', es: 'Pisos' },
  'Cabinets & Counters': { en: 'Cabinets & Counters', es: 'Gabinetes y Cubiertas' },
  'MEP Trim': { en: 'MEP Trim', es: 'Acabados MEP' },
  'Exterior': { en: 'Exterior', es: 'Exterior' },
  'Landscaping': { en: 'Landscaping', es: 'Jardinería' },
  'Final Inspection': { en: 'Final Inspection', es: 'Inspección Final' },
  // Closeout
  'Closeout / Sale': { en: 'Closeout / Sale', es: 'Cierre / Venta' },
}

// Ordered, specificity-first rules. The FIRST rule with a matching keyword wins,
// so more specific multi-word rules (e.g. "mep trim" → finishes) must precede
// the generic single-word rule ("mep" → rough-in). Keywords are matched as
// case-insensitive substrings against the phase label. EN + ES keywords are
// included so the demo schedule (which carries real Spanish phase labels) and
// real projects (English phase labels) both rank coherently.
const PHASE_RANK_RULES: { rank: number; keywords: string[] }[] = [
  // A. Preconstruction / feasibility
  { rank: 10, keywords: [
    'pre-con', 'precon', 'pre con', 'feasib', 'due diligence', 'lot ', 'land acquis',
    'survey', 'topograf', 'architect', 'arquitect', 'design', 'diseño', 'plan set',
    'plans', 'planos', 'engineer', 'ingenier', 'permit', 'permiso', 'bid', 'cotiza',
    'licitaci', 'contract', 'contrato', 'insurance', 'seguro', 'coi', 'selection', 'selecci',
  ] },
  // Temporary power / T-pole (electrical service stub — precedes the slab)
  { rank: 25, keywords: ['temporary power', 'temp power', 't-pole', 'tpole', 't pole'] },
  // B. Site preparation
  { rank: 20, keywords: ['site', 'sitio', 'grad', 'nivelaci', 'excavat', 'excavaci', 'demolition', 'clearing', 'rough grade'] },
  // C. Foundation
  { rank: 30, keywords: ['foundation', 'cimentaci', 'under slab', 'slab', 'losa', 'footing', 'zapata', 'pier', 'flatwork'] },
  // D. Framing
  { rank: 40, keywords: ['fram', 'estructura', 'structur'] },
  // E. Roofing / dry-in
  { rank: 50, keywords: ['roof', 'techo', 'dry-in', 'dry in', 'dried-in'] },
  // F. Exterior shell
  { rank: 55, keywords: ['exterior', 'siding', 'masonry', 'mamposter', 'brick', 'ladrillo', 'stucco', 'fachada'] },
  // MEP trim / fixtures — specificity: a finishes-stage task; must beat generic "mep"
  { rank: 90, keywords: ['mep trim', 'trim & fixture', 'trim and fixture', 'mep finish'] },
  // G. MEP rough-in
  { rank: 60, keywords: ['mep', 'plumb', 'plomer', 'electric', 'ctric', 'hvac', 'mechanical', 'mecánic', 'meter'] },
  // H. Insulation
  { rank: 70, keywords: ['insulat', 'aislant', 'aislamiento'] },
  // I. Drywall
  { rank: 80, keywords: ['drywall', 'tablaroca', 'sheetrock', 'texture', 'textura'] },
  // J. Interior finishes
  { rank: 90, keywords: [
    'finish', 'acabado', 'paint', 'pintura', 'cabinet', 'gabinete', 'counter', 'encimera',
    'floor', 'piso', 'tile', 'azulejo', 'trim', 'fixture', 'millwork',
  ] },
  // K. Landscaping / sitework finishes
  { rank: 95, keywords: ['landscap', 'paisaj', 'jard', 'sod', 'irrigation', 'riego', 'driveway', 'fence', 'fencing'] },
  // L. Closeout / final
  { rank: 100, keywords: [
    'final', 'punch', 'closeout', 'cierre', 'certificate', 'occupancy', 'ocupaci',
    'utility', 'walkthrough', 'handover', 'entrega',
  ] },
]

/**
 * Rank a phase label into the generic residential construction sequence.
 * Lower rank = earlier in the build. Unknown labels return PHASE_UNKNOWN_RANK
 * so they sort after recognised phases while keeping their original order
 * under a stable sort.
 */
export function phaseRank(phase: string): number {
  const key = (phase ?? '').trim()
  // Numeric macro-stage keys ("0".."4") rank by their explicit stage order.
  const mapped = NUMERIC_PHASE_LABELS[key]
  if (mapped) return mapped.rank
  // Any other purely-numeric key keeps numeric order as a safety net so a bare
  // number never lands in the unstable unknown bucket.
  if (/^\d+$/.test(key)) return 5 + Number(key)

  const p = key.toLowerCase()
  for (const rule of PHASE_RANK_RULES) {
    for (const kw of rule.keywords) {
      if (p.includes(kw)) return rule.rank
    }
  }
  return PHASE_UNKNOWN_RANK
}

/**
 * Human-readable, bilingual group header for a phase label.
 *
 * - Numeric macro-stage keys ("0".."4") map to readable EN/ES labels.
 * - Any other numeric key becomes "Phase N" / "Fase N" so a raw number is
 *   never shown to the user.
 * - Descriptive phase labels are localized via the task's Spanish phase
 *   (`phaseEs`) when the UI is in Spanish, and the base label otherwise. This
 *   keeps localization fully language-reactive: callers group by the raw,
 *   language-independent `phase` key and pick the displayed label here using
 *   the CURRENT `isEs`, so toggling the language updates the header live.
 */
export function phaseDisplayLabel(phase: string, phaseEs: string | null | undefined, isEs: boolean): string {
  const key = (phase ?? '').trim()
  const mapped = NUMERIC_PHASE_LABELS[key]
  if (mapped) return isEs ? mapped.es : mapped.en
  if (/^\d+$/.test(key)) return isEs ? `Fase ${key}` : `Phase ${key}`
  // Known descriptive stage → canonical bilingual header.
  const desc = DESCRIPTIVE_PHASE_LABELS[key]
  if (desc) return isEs ? desc.es : desc.en
  // Unknown descriptive label — use the Spanish variant in ES, the base in EN.
  return isEs ? (phaseEs || phase) : phase
}

// ── Macro phase (display-only grouping) ──────────────────────────────────────
// The Gantt shows a 3-level hierarchy: macro phase → subphase → task. The macro
// is DERIVED from the existing subphase value (no backend/schema change). Used
// only to render the macro header band; bars stay colored by subphase.
export type GanttMacroPhase = 'preconstruction' | 'construction' | 'closeout'

export const MACRO_PHASE_ORDER: GanttMacroPhase[] = ['preconstruction', 'construction', 'closeout']

const MACRO_PHASE_LABELS: Record<GanttMacroPhase, { en: string; es: string }> = {
  preconstruction: { en: 'Preconstruction', es: 'Preconstrucción' },
  construction: { en: 'Construction', es: 'Construcción' },
  closeout: { en: 'Closeout / Sale', es: 'Cierre / Venta' },
}

// Explicit macro for every subphase the app produces (numeric seed, Base/Classic
// descriptive, jurisdiction PHASE_LABELS, demo). Generic only — no jurisdiction
// names. Unknown phases fall back by rank (preconstruction < site work).
const MACRO_BY_PHASE: Record<string, GanttMacroPhase> = {
  // Numeric seed macro-stages
  '0': 'preconstruction', '1': 'preconstruction', '2': 'preconstruction',
  '3': 'construction', '4': 'closeout',
  // Preconstruction
  'Land / Due Diligence': 'preconstruction',
  'Plans & Permits': 'preconstruction',
  'Bids, Contracts & Insurance': 'preconstruction',
  'Pre-Construction': 'preconstruction',
  // Construction subphases (Base/Classic + jurisdiction + demo)
  'Site Work': 'construction', 'Site': 'construction', 'Temporary Power': 'construction',
  'Under Slab': 'construction', 'Foundation': 'construction', 'Framing': 'construction',
  'Frame Rough': 'construction', 'Roofing': 'construction', 'MEP Rough-In': 'construction',
  'MEP': 'construction', 'Insulation': 'construction', 'Drywall': 'construction',
  'Interior Paint': 'construction', 'Flooring': 'construction', 'Cabinets & Counters': 'construction',
  'MEP Trim': 'construction', 'Finishes': 'construction', 'Exterior': 'construction',
  'Landscaping': 'construction', 'Meter Release': 'construction', 'Utility': 'construction',
  'Final Inspection': 'construction', 'Final': 'construction',
  // Closeout
  'Closeout / Sale': 'closeout', 'Closeout': 'closeout',
}

/**
 * Derive the macro phase for a subphase key. Known subphases map explicitly;
 * unknown ones fall back by rank (preconstruction-keyword phases rank < 20),
 * defaulting to Construction (closeout is only ever assigned explicitly).
 */
export function macroPhase(phase: string): GanttMacroPhase {
  const key = (phase ?? '').trim()
  const mapped = MACRO_BY_PHASE[key]
  if (mapped) return mapped
  return phaseRank(key) < 20 ? 'preconstruction' : 'construction'
}

/** Bilingual macro-phase header label. */
export function macroPhaseLabel(macro: GanttMacroPhase, isEs: boolean): string {
  const l = MACRO_PHASE_LABELS[macro]
  return isEs ? l.es : l.en
}
